import { useCallback, useEffect, useState } from 'react';
import { marketplaceApi, pollOrderUntilSettled, type OrderSecrets, type OrderSummary } from '../api/marketplace';
import { haptic, openCheckoutLink, openInvoice } from '../lib/telegram';
import { StarIcon } from '../features/tabs/icons';
import { ErrorState, Loader } from '../components/States';
import { ReviewForm } from '../features/reviews/ReviewsBlock';
import './PurchasesPage.css';

interface PurchasesPageProps {
  /** Меняется после успешной оплаты в каталоге — триггер перезагрузки. */
  refreshKey: number;
  onOpenChat?: (orderId: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  AWAITING_PAYMENT: 'Ждёт оплаты',
  DELIVERED: 'Выдан',
  REFUNDED: 'Возврат',
  FAILED: 'Ошибка',
  CANCELLED: 'Отменён',
};

export function PurchasesPage({ refreshKey, onOpenChat }: PurchasesPageProps) {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secrets, setSecrets] = useState<Record<string, OrderSecrets>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);
  const [reviewFor, setReviewFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await marketplaceApi.orders();
      setOrders(response.items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить покупки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  /**
   * Ключи запрашиваются отдельным эндпоинтом и только по явному действию:
   * список заказов не должен таскать секреты по сети «на всякий случай».
   */
  const reveal = async (orderId: string): Promise<void> => {
    if (secrets[orderId]) return;

    setRevealingId(orderId);

    try {
      const response = await marketplaceApi.secrets(orderId);
      setSecrets((current) => ({ ...current, [orderId]: response }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось получить ключи');
    } finally {
      setRevealingId(null);
    }
  };

  const payPending = async (order: OrderSummary): Promise<void> => {
    if (!order.invoiceUrl) return;

    if (order.paymentKind === 'tribute-webapp') {
      openCheckoutLink(order.invoiceUrl);
      const settled = await pollOrderUntilSettled(order.id);

      if (settled.status === 'DELIVERED') {
        haptic('success');
      } else if (settled.status === 'FAILED' || settled.status === 'CANCELLED') {
        haptic('error');
        setError('Платёж не прошёл');
      }

      await load();
      return;
    }

    const status = await openInvoice(order.invoiceUrl);

    if (status === 'paid') {
      haptic('success');
      await load();
    }
  };

  const copy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      haptic('success');
    } catch {
      // Вебвью может не дать доступ к буферу без жеста — не считаем ошибкой,
      // пользователь всегда может выделить текст вручную.
      haptic('warning');
    }
  };

  if (isLoading && orders.length === 0) return <Loader />;
  if (error && orders.length === 0) return <ErrorState message={error} onRetry={() => void load()} />;

  if (orders.length === 0) {
    return (
      <div className="purchases purchases--empty">
        <p className="purchases__empty-title">Пока пусто</p>
        <p className="purchases__empty-hint">Оформи заказ в каталоге — ключ придёт сразу после оплаты</p>
      </div>
    );
  }

  return (
    <div className="purchases">
      <h1 className="page-title">Мои покупки</h1>

      {orders.map((order) => {
        const revealed = secrets[order.id];

        return (
          <article key={order.id} className="order">
            <header className="order__header">
              <div>
                <p className="order__number">{order.number}</p>
                <p className="order__date">{new Date(order.createdAt).toLocaleString('ru-RU')}</p>
              </div>

              <span className={`order__status order__status--${order.status.toLowerCase()}`}>
                {STATUS_LABELS[order.status] ?? order.status}
              </span>
            </header>

            <ul className="order__items">
              {order.items.map((item) => (
                <li key={item.id} className="order__item">
                  <span>{item.title}</span>
                  <span className="order__item-price">
                    <StarIcon size={12} />
                    {item.unitPrice}
                  </span>
                </li>
              ))}
            </ul>

            {order.status === 'DELIVERED'
              ? uniqueReviewable(order.items).map((item) => {
                    const productId = item.product!.id;
                    const open = reviewFor === `${order.id}:${item.id}`;

                    return (
                      <div key={`review-${item.id}`}>
                        <button
                          type="button"
                          className="order__action order__action--quiet"
                          onClick={() => setReviewFor(open ? null : `${order.id}:${item.id}`)}
                        >
                          {item.review ? `Отзыв: ${item.review.rating}/5` : `Оставить отзыв: ${item.title}`}
                        </button>
                        {open ? (
                          <ReviewForm
                            compact
                            productId={productId}
                            orderId={order.id}
                            initial={item.review}
                            onSaved={() => {
                              setReviewFor(null);
                              void load();
                            }}
                          />
                        ) : null}
                      </div>
                    );
                  })
              : null}

            {order.status === 'DELIVERED' ? (
              revealed ? (
                <div className="order__secrets">
                  {revealed.items.map((item) => (
                    <div key={item.id} className="secret">
                      <p className="secret__label">{item.title}</p>
                      <button type="button" className="secret__value" onClick={() => void copy(item.secret ?? '')}>
                        <code>{item.secret ?? '—'}</code>
                        <span className="secret__copy">копировать</span>
                      </button>
                      {item.instructions ? <p className="secret__hint">{item.instructions}</p> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  className="order__action"
                  disabled={revealingId === order.id}
                  onClick={() => void reveal(order.id)}
                >
                  {revealingId === order.id ? 'Загружаю…' : 'Показать ключи'}
                </button>
              )
            ) : null}

            {order.status === 'DELIVERED' || order.status === 'REFUNDED' || order.status === 'FAILED' ? (
              <button type="button" className="order__action order__action--quiet" onClick={() => onOpenChat?.(order.id)}>
                Чат по заказу
              </button>
            ) : null}

            {order.status === 'AWAITING_PAYMENT' && order.invoiceUrl ? (
              <>
                <button
                  type="button"
                  className="order__action"
                  onClick={() => {
                    void payPending(order);
                  }}
                >
                  Оплатить
                </button>
                <p className="order__hint">
                  Счёт ещё жив. {order.expiresAt ? `Резерв до ${new Date(order.expiresAt).toLocaleTimeString('ru-RU')}.` : ''}
                </p>
              </>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function uniqueReviewable(items: OrderSummary['items']): OrderSummary['items'] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.canReview || !item.product) return false;
    if (seen.has(item.product.id)) return false;
    seen.add(item.product.id);
    return true;
  });
}
